import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight, Loader2, User, Flag, MessageSquare, X, Calendar, AlignLeft, Clock, ArrowRight, Save } from "lucide-react";
import { api } from "@/lib/api";

const COLUMNS = [
  { key: "new", label: "Нові", accent: "border-t-slate-400" },
  { key: "in_progress", label: "В роботі", accent: "border-t-[#0F76BC]" },
  { key: "done", label: "Виконано", accent: "border-t-emerald-500" },
];

const PRIORITIES = {
  low: { label: "Низький", cls: "bg-slate-100 text-slate-500" },
  normal: { label: "Звичайний", cls: "bg-[#0F76BC]/10 text-[#0F76BC]" },
  high: { label: "Високий", cls: "bg-red-50 text-red-500" },
};

const ORDER = ["new", "in_progress", "done"];

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function TaskDetail({ task, onClose, onSaved, onDeleted, onOpenChat }) {
  const [form, setForm] = useState({
    title: task.title || "",
    description: task.description || "",
    status: task.status || "new",
    priority: task.priority || "normal",
    assignee: task.assignee || "",
    due_date: task.due_date || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/tasks/${task.id}`, {
        title: form.title,
        description: form.description,
        status: form.status,
        priority: form.priority,
        assignee: form.assignee || null,
        due_date: form.due_date || null,
      });
      onSaved(data.task);
      toast.success("Задачу збережено");
      onClose();
    } catch {
      toast.error("Не вдалося зберегти");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/tasks/${task.id}`);
      onDeleted(task.id);
      toast.success("Задачу видалено");
      onClose();
    } catch {
      toast.error("Помилка видалення");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="task-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-[14px] shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="font-head font-bold text-lg">Картка задачі</h3>
          <button onClick={onClose} data-testid="task-detail-close" className="text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Назва</label>
            <input data-testid="detail-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1.5"><AlignLeft size={13} /> Опис</label>
            <textarea data-testid="detail-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Деталі задачі..." className="w-full border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Статус</label>
              <select data-testid="detail-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]">
                <option value="new">Нова</option><option value="in_progress">В роботі</option><option value="done">Виконано</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1.5"><Flag size={13} /> Пріоритет</label>
              <select data-testid="detail-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]">
                <option value="low">Низький</option><option value="normal">Звичайний</option><option value="high">Високий</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1.5"><User size={13} /> Виконавець</label>
              <input data-testid="detail-assignee" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="w-full border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1.5"><Calendar size={13} /> Дедлайн</label>
              <input data-testid="detail-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
            </div>
          </div>

          {task.contact_name && (
            <div className="bg-[#0F76BC]/5 border border-[#0F76BC]/15 rounded-[10px] p-3 flex items-center justify-between gap-2">
              <div className="text-sm text-slate-700 flex items-center gap-2 min-w-0">
                <MessageSquare size={15} className="text-[#0F76BC] shrink-0" />
                <span className="truncate">Прив'язано до чату: <b>{task.contact_name}</b></span>
              </div>
              {task.cid && (
                <button data-testid="detail-open-chat" onClick={() => onOpenChat(task.cid)} className="shrink-0 flex items-center gap-1 text-[#0F76BC] font-semibold text-sm hover:underline">
                  Відкрити <ArrowRight size={14} />
                </button>
              )}
            </div>
          )}

          <div className="text-[11px] text-slate-400 flex items-center gap-1.5"><Clock size={12} /> Створено {fmtDateTime(task.created_at)}{task.created_by ? ` · ${task.created_by}` : ""}</div>
        </div>
        <div className="flex gap-2 p-5 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={del} data-testid="detail-delete" className="flex items-center gap-1.5 px-4 py-2.5 rounded-[10px] text-sm font-semibold text-red-500 border border-red-200 hover:bg-red-50 transition-colors"><Trash2 size={15} /> Видалити</button>
          <button onClick={save} disabled={saving || !form.title.trim()} data-testid="detail-save" className="ml-auto flex items-center gap-2 bg-[#0F76BC] hover:bg-[#0c5f96] text-white px-6 py-2.5 rounded-[10px] text-sm font-semibold transition-colors disabled:opacity-50">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Зберегти</button>
        </div>
      </div>
    </div>
  );
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("normal");
  const [creating, setCreating] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/tasks");
      setTasks(data.tasks);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/tasks", {
        title: title.trim(),
        assignee: assignee.trim() || null,
        priority,
      });
      setTasks((p) => [data.task, ...p]);
      setTitle("");
      setAssignee("");
      setPriority("normal");
      toast.success("Задачу створено");
    } catch {
      toast.error("Не вдалося створити задачу");
    } finally {
      setCreating(false);
    }
  };

  const move = async (task, dir) => {
    const idx = ORDER.indexOf(task.status);
    const next = ORDER[idx + dir];
    if (!next) return;
    setTasks((p) => p.map((t) => (t.id === task.id ? { ...t, status: next } : t)));
    try {
      await api.put(`/admin/tasks/${task.id}`, { status: next });
    } catch {
      toast.error("Помилка оновлення");
      load();
    }
  };

  const remove = async (task) => {
    setTasks((p) => p.filter((t) => t.id !== task.id));
    try {
      await api.delete(`/admin/tasks/${task.id}`);
    } catch {
      toast.error("Помилка видалення");
      load();
    }
  };

  const onSaved = (updated) => setTasks((p) => p.map((t) => (t.id === updated.id ? updated : t)));
  const onDeleted = (id) => setTasks((p) => p.filter((t) => t.id !== id));
  const openTaskChat = (cid) => navigate(`/admin?cid=${encodeURIComponent(cid)}`);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        <div>
          <h1 className="font-head text-2xl md:text-3xl font-bold tracking-tight">Задачі</h1>
          <p className="text-slate-500 text-sm mt-1">Керуйте задачами команди провайдера</p>
        </div>

        <form onSubmit={create} className="bg-white border border-slate-200 rounded-[12px] p-4 shadow-sm flex flex-col sm:flex-row gap-3">
          <input
            data-testid="task-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Назва задачі (напр. Підключити абонента на вул. Шевченка)"
            className="flex-1 border border-slate-200 rounded-[10px] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC] transition-[border-color,box-shadow]"
          />
          <input
            data-testid="task-assignee-input"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            placeholder="Виконавець"
            className="sm:w-40 border border-slate-200 rounded-[10px] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]"
          />
          <select
            data-testid="task-priority-select"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="sm:w-40 border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]"
          >
            <option value="low">Низький</option>
            <option value="normal">Звичайний</option>
            <option value="high">Високий</option>
          </select>
          <button
            data-testid="create-task-btn"
            type="submit"
            disabled={creating}
            className="bg-[#0F76BC] hover:bg-[#0c5f96] text-white px-5 py-2.5 rounded-[10px] font-semibold text-sm transition-[background-color,transform] active:scale-95 shadow-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {creating ? <Loader2 size={17} className="animate-spin" /> : <Plus size={17} />}
            Додати
          </button>
        </form>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-300" size={28} /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tasks-board">
            {COLUMNS.map((col) => {
              const colTasks = tasks.filter((t) => t.status === col.key);
              return (
                <div key={col.key} data-testid={`column-${col.key}`} className={`bg-slate-100/60 rounded-[12px] border-t-4 ${col.accent} p-3`}>
                  <div className="flex items-center justify-between px-1 mb-3">
                    <h2 className="font-head font-bold text-slate-700">{col.label}</h2>
                    <span className="text-xs font-bold text-slate-400 bg-white rounded-full px-2 py-0.5">{colTasks.length}</span>
                  </div>
                  <div className="space-y-2.5 min-h-[60px]">
                    {colTasks.length === 0 && (
                      <div className="text-center text-slate-300 text-xs py-6">Немає задач</div>
                    )}
                    {colTasks.map((t) => {
                      const idx = ORDER.indexOf(t.status);
                      const pr = PRIORITIES[t.priority] || PRIORITIES.normal;
                      return (
                        <div key={t.id} data-testid={`task-${t.id}`} onClick={() => setSelectedTask(t)} className="bg-white border border-slate-200 rounded-[10px] p-3 shadow-sm group cursor-pointer hover:border-[#0F76BC]/40 transition-colors">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium text-slate-800 ${t.status === "done" ? "line-through text-slate-400" : ""}`}>{t.title}</p>
                            <button data-testid={`delete-task-${t.id}`} onClick={(e) => { e.stopPropagation(); remove(t); }}
                              className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-slate-300 hover:text-red-500 focus-visible:text-red-500 transition-[opacity,color] shrink-0">
                              <Trash2 size={15} />
                            </button>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-2.5">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${pr.cls}`}>
                              <Flag size={10} /> {pr.label}
                            </span>
                            {t.assignee && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                <User size={11} /> {t.assignee}
                              </span>
                            )}
                          </div>
                          {t.contact_name && (
                            <div className="mt-2 text-[11px] text-[#0F76BC] flex items-center gap-1">
                              <MessageSquare size={11} /> З чату: {t.contact_name}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-slate-100">
                            <button data-testid={`move-left-${t.id}`} onClick={(e) => { e.stopPropagation(); move(t, -1); }} disabled={idx === 0}
                              className="w-7 h-7 rounded-[8px] flex items-center justify-center text-slate-400 hover:bg-slate-100 disabled:opacity-30 transition-colors">
                              <ChevronLeft size={16} />
                            </button>
                            <button data-testid={`move-right-${t.id}`} onClick={(e) => { e.stopPropagation(); move(t, 1); }} disabled={idx === ORDER.length - 1}
                              className="w-7 h-7 rounded-[8px] flex items-center justify-center text-[#0F76BC] hover:bg-[#0F76BC]/10 disabled:opacity-30 transition-colors">
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedTask && (
          <TaskDetail
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
            onSaved={onSaved}
            onDeleted={onDeleted}
            onOpenChat={openTaskChat}
          />
        )}
      </div>
    </div>
  );
}
